import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormControl } from '../../../../components/common/FormControl'
import { Modal } from '../../../../components/common/Modal'
import { ModalBody } from '../../../../components/common/Modal/ModalBody'
import { ModalHeader } from '../../../../components/common/Modal/ModalHeader'
import { SelectDropdown } from '../../../../components/common/SelectDropdown'
import { TextInput } from '../../../../components/common/TextInput'
import { Button, ButtonVariant } from '../../../../components/common/Button'
import { ToastType } from '../../../../components/common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import {
  ManagementPartner,
  getPartnerId,
} from '../../../../components/society/ManagementPartner/management_partner'
import { adminDiscountSchema } from '../../../../forms/schemas/admin_discount'
import {
  adminQueries,
  createDiscount as createDiscountRequest,
} from '../../../../api/admin'

interface CreateDiscountModalProps {
  show: boolean
  close: () => void
}

export const CreateDiscountModal = ({
  show,
  close,
}: CreateDiscountModalProps): React.ReactNode => {
  const queryClient = useQueryClient()
  const { setToastNotification } = useToastNotifications()
  const { getErrorMessage } = useFormErrorMessage()

  const defaultDiscountCodeValues = {
    code: '',
    discount: 0,
    partnerNameId: ManagementPartner.BOSTADSRATTERNA,
    articleNo: '',
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<InferType<typeof adminDiscountSchema>>({
    mode: 'all',
    resolver: yupResolver(adminDiscountSchema),
    defaultValues: defaultDiscountCodeValues,
  })

  const partnerOptions = Object.values(ManagementPartner).map((partner) => ({
    value: partner,
    label: partner,
  }))

  const closeCreateDiscountCodeModal = (): void => {
    close()
    reset(defaultDiscountCodeValues)
  }

  const { mutate: mutateCreateDiscount, isPending } = useMutation({
    mutationFn: createDiscountRequest,
    onSuccess: async () => {
      setToastNotification(ToastType.SUCCESS, 'Discount code created')
      closeCreateDiscountCodeModal()
      await queryClient.invalidateQueries({
        queryKey: adminQueries.getDiscountCodes().queryKey,
      })
    },
    onError: () => {
      setToastNotification(ToastType.DANGER, 'Could not create discount code')
    },
  })

  const onSubmit = (data: InferType<typeof adminDiscountSchema>): void => {
    mutateCreateDiscount({
      code: data.code,
      discount: Number(data.discount),
      partnerId: getPartnerId(data.partnerNameId as ManagementPartner),
      articleNo: data.articleNo,
    })
  }

  return (
    <Modal show={show} size="lg">
      {{
        header: (
          <ModalHeader onClose={closeCreateDiscountCodeModal}>
            Skapa rabattkod
          </ModalHeader>
        ),
        body: (
          <ModalBody>
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                control={control}
                name="code"
                render={({ field: { value, name, onChange, onBlur } }) => (
                  <FormControl
                    label="Kod"
                    name={name}
                    error={errors.code && getErrorMessage(errors.code)}
                  >
                    <TextInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.code}
                    />
                  </FormControl>
                )}
              />

              <Controller
                control={control}
                name="discount"
                render={({ field: { value, name, onChange, onBlur } }) => (
                  <FormControl
                    label="Rabatt (%)"
                    name={name}
                    error={errors.discount && getErrorMessage(errors.discount)}
                  >
                    <TextInput
                      type="number"
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.discount}
                      disableOnWheel
                    />
                  </FormControl>
                )}
              />

              <Controller
                control={control}
                name="partnerNameId"
                render={({ field: { value, name, onChange } }) => (
                  <FormControl
                    label="Partner"
                    name={name}
                    error={
                      errors.partnerNameId &&
                      getErrorMessage(errors.partnerNameId)
                    }
                  >
                    <SelectDropdown
                      options={partnerOptions}
                      value={partnerOptions.find(
                        (partner) => partner.value === value
                      )}
                      onChange={(option) => onChange(option?.value)}
                      error={!!errors.partnerNameId}
                    />
                  </FormControl>
                )}
              />

              <Controller
                control={control}
                name="articleNo"
                render={({ field: { value, name, onChange, onBlur } }) => (
                  <FormControl
                    label="Article no"
                    name={name}
                    error={
                      errors.articleNo && getErrorMessage(errors.articleNo)
                    }
                  >
                    <TextInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.articleNo}
                    />
                  </FormControl>
                )}
              />

              <div className="flex justify-between">
                <Button
                  type="submit"
                  variant={ButtonVariant.PRIMARY}
                  disabled={!isValid || isPending}
                  loading={isPending}
                  label="Skapa"
                />
              </div>
            </form>
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
